import { type Track } from "@lib/types";

import { evaluatorIsMeanRecommender } from "./accessors";
import { type Disclosure, type RoundMember } from "./aggregate";

export type RecusalDecisionResult = {
  decision: Disclosure["decision"];
  decisionReason: "self-recused" | "recuse-quorum" | "keep-quorum" | "pending";
  eligibleVoterCount: number;
  nonAbstainingCount: number;
  quorum: number;
  recuseCount: number;
  keepCount: number;
  abstainCount: number;
};

export function isEligibleRecusalVoter(
  member: RoundMember,
  voteSubject: RoundMember,
  voterTrack: Track,
): boolean {
  const isInRelevantTrack =
    voteSubject.track === "NO_TRACK"
      ? member.track !== "NO_TRACK"
      : member.track === voterTrack;

  return (
    member.id !== voteSubject.id &&
    isInRelevantTrack &&
    member.status === "active" &&
    Object.values(member.evaluators).some(
      (evaluator) =>
        evaluator.type === "recommender" &&
        !evaluatorIsMeanRecommender(evaluator),
    )
  );
}

export function recusalVoters(
  roundMembers: RoundMember[],
  voteSubject: RoundMember,
  voterTrack: Track,
) {
  return roundMembers.filter((member) =>
    isEligibleRecusalVoter(member, voteSubject, voterTrack),
  );
}

export function recusalQuorum(
  eligibleVoterCount: number,
  abstainCount: number,
) {
  return Math.ceil((eligibleVoterCount - abstainCount + 0.1) / 2);
}

export function recusalDecision(
  voters: RoundMember[],
  disclosure: Pick<Disclosure, "selfRecused" | "votes">,
): RecusalDecisionResult {
  let recuseCount = 0;
  let keepCount = 0;
  let abstainCount = 0;
  for (const member of voters) {
    const memberVote = disclosure.votes[member.id];
    if (memberVote === "y") recuseCount++;
    else if (memberVote === "n") keepCount++;
    else if (memberVote === "a") abstainCount++;
  }

  const quorum = recusalQuorum(voters.length, abstainCount);
  const decision =
    disclosure.selfRecused || recuseCount >= quorum
      ? "recused"
      : keepCount >= quorum
        ? "not-recused"
        : null;
  const decisionReason = disclosure.selfRecused
    ? "self-recused"
    : recuseCount >= quorum
      ? "recuse-quorum"
      : keepCount >= quorum
        ? "keep-quorum"
        : "pending";

  return {
    decision,
    decisionReason,
    eligibleVoterCount: voters.length,
    nonAbstainingCount: voters.length - abstainCount,
    quorum,
    recuseCount,
    keepCount,
    abstainCount,
  };
}
